import { LandingComponentsConfig, LandingScreenSchema } from '@/modules/Landing/instances/@types/landing-types'
import alertGiftPng from '@/assets/landing/alert-gift.png'
import age18Png from '@/assets/landing-age-selection/lymphatic-walking/age-18.png'
import age40Png from '@/assets/landing-age-selection/lymphatic-walking/age-40.png'
import age50Png from '@/assets/landing-age-selection/lymphatic-walking/age-50.png'
import age60Png from '@/assets/landing-age-selection/lymphatic-walking/age-60.png'

/**
 * Printable variant of `age-lymphatic-walking-f`: same art and same age buckets, plus the two
 * changes the Figma Dev Mode annotations ask for on `g-input-activity` (23768:72294) —
 * "add banner" (Development) and "update text" (Copy).
 *
 * Structure follows the other printable age pickers (`age-printable-walking-f`,
 * `age-tai-chi-walking-f-pr`): `LandingAlert` + `LandingTitleExtended`, replacing the base
 * funnel's `LandingTitle` + `LandingSubtitle` pair.
 */
const schema: LandingScreenSchema = {
	order: ['LandingHead', 'LandingAlert', 'LandingTitleExtended', 'LandingAgeGrid'],

	components: {
		// Figma 23768:96920: Fill/Accent-16 pill, gift icon, "printable guide" in Text/Accent
		// (#7d8bf7). The existing key already carries that accent <span>, so no new copy is minted.
		LandingAlert: ({ $t }): LandingComponentsConfig['LandingAlert'] => ({
			text: $t('female_yoga.pages.landing.alert_text'),
			icon: alertGiftPng,
			backgroundColor: 'var(--Fill-Accent-16)',
		}),

		// `LandingTitleExtended` rather than `LandingTitle` + `LandingSubtitle`: its title is
		// 30px / uppercase / 700 — the exact Figma spec (23768:72303) — and `note` renders the
		// 18px Text-Primary-60 line. The base funnel's `female_activity…step_1.subtitle`
		// ("BASED ON YOUR {age}") is dropped in favour of the quiz note.
		//
		// The title key's EN value carries a manual `<br/>` after "Lymphatic", so this renders
		// "LYMPHATIC / WALKING PLAN" where Figma wraps as "LYMPHATIC WALKING / PLAN". Same words;
		// the key is reused as-is because it is already translated in all 14 locales.
		LandingTitleExtended: ({ $t }): LandingComponentsConfig['LandingTitleExtended'] => ({
			title: $t('main_walking.lymphatic.age_picker.title'),
			note: $t('female_yoga.pages.landing.quiz'),
		}),

		// Four clickable cards, no otherAgesButton — Figma promotes 18-39 out of the escape link
		// and collapses 60-69 + 70+ into a single "60+" card, whose analyticValue reuses the 60-80
		// bucket introduced by DEV-34798 so no new analytics value is created.
		// The 18_39 / 60_plus keys are the pair DEV-34967 (PR #672) introduced — reused here rather
		// than duplicated. They are still pending on the localization platform.
		LandingAgeGrid: ({ $t }): LandingComponentsConfig['LandingAgeGrid'] => ({
			theme: 'purple',
			ageRanges: [
				{
					value: 18,
					analyticValue: '18-39',
					label: $t('female_yoga.pages.landing.age_range.18_39'),
					img: age18Png,
				},
				{
					value: 40,
					analyticValue: '40-49',
					label: $t('female_yoga.pages.landing.age_range.40_49'),
					img: age40Png,
				},
				{
					value: 50,
					analyticValue: '50-59',
					label: $t('female_yoga.pages.landing.age_range.50_59'),
					img: age50Png,
				},
				{
					value: 60,
					analyticValue: '60-80',
					label: $t('female_yoga.pages.landing.age_range.60_plus'),
					img: age60Png,
				},
			],
		}),
	},
}

export default schema
